import { useEffect, useState } from "react";
import { Page, PageStatus, Tooltip } from "src/components";
import {
  Color,
  DateInput,
  Flex,
  Pagination,
  Search,
  Select,
  SubgridTable,
  Text,
} from "src/elements";
import { SubgridColumn } from "src/elements/SubgridTable/store/types";
import { useApi } from "src/api";
import { useLoaders } from "src/components/InlineLoader";
import { useDebounceEffect } from "src/hooks/useDebounceEffect";
import { dateIsoStringToLocaleString } from "src/utils";
import { useSearchParams } from "src/utils/useSearchParams";
import { useNavigation } from "src/utils/navigation";
import { useUser } from "src/atoms/user";
import { hasPermission, PermissionCode } from "@CommonUtils/RolesPermissions";
import { pages } from "src/utils/pages";
import { AuditLogEntry, ResponseGetAuditLogs, RouteGetAuditLogs } from "@CommonApi/audit-logs";
import { AuditActionCode, AuditResourceType } from "@CommonUtils/AuditActionCodes";
import { AUDIT_ACTION_CODE_LABELS, AUDIT_RESOURCE_TYPE_LABELS } from "./store/labels";
import {
  ACTION_CODE_OPTIONS,
  PAGE_SIZE,
  RESOURCE_TYPE_OPTIONS,
  STATUS_OPTIONS,
} from "./store/constants";

type FilterKey = "dateFrom" | "dateTo" | "actionCode" | "resourceType" | "status" | "search";

interface StatusCodeCellProps {
  statusCode: number;
}

const StatusCodeCell = ({ statusCode }: StatusCodeCellProps) => {
  const isSuccess = statusCode < 400;
  return (
    <Text variant="small2" color={isSuccess ? Color.success : Color.red}>
      {statusCode}
    </Text>
  );
};

interface PathCellProps {
  path: string;
}

const PathCell = ({ path }: PathCellProps) => (
  <Flex position="relative" width="100%" overflow="hidden">
    <Text variant="small2" lineClamp={1}>
      {path}
    </Text>
    <Tooltip>{path}</Tooltip>
  </Flex>
);

const AUDIT_LOG_COLUMNS: SubgridColumn<AuditLogEntry>[] = [
  {
    id: "createdAt",
    header: "Date",
    width: "160px",
    renderer: ({ row }) => (
      <Text variant="small2">{dateIsoStringToLocaleString(row.createdAt)}</Text>
    ),
  },
  {
    id: "actorEmail",
    header: "Actor",
    width: "1fr",
    renderer: ({ row }) => <Text variant="small2">{row.actorEmail ?? "System"}</Text>,
  },
  {
    id: "actionCode",
    header: "Action",
    width: "200px",
    renderer: ({ row }) => (
      <Text variant="small2">{AUDIT_ACTION_CODE_LABELS[row.actionCode] ?? row.actionCode}</Text>
    ),
  },
  {
    id: "statusCode",
    header: "Status",
    width: "90px",
    renderer: ({ row }) => <StatusCodeCell statusCode={row.statusCode} />,
  },
  {
    id: "resourceType",
    header: "Resource",
    width: "140px",
    renderer: ({ row }) => (
      <Text variant="small2">
        {row.resourceType
          ? (AUDIT_RESOURCE_TYPE_LABELS[row.resourceType] ?? row.resourceType)
          : "—"}
      </Text>
    ),
  },
  {
    id: "resourceId",
    header: "Resource ID",
    width: "110px",
    renderer: ({ row }) => <Text variant="small2">{row.resourceId ?? "—"}</Text>,
  },
  {
    id: "httpMethod",
    header: "Method",
    width: "70px",
    renderer: ({ row }) => <Text variant="small2">{row.httpMethod}</Text>,
  },
  {
    id: "httpPath",
    header: "Path",
    width: "1fr",
    renderer: ({ row }) => <PathCell path={row.httpPath} />,
  },
  {
    id: "ipAddress",
    header: "IP",
    width: "120px",
    renderer: ({ row }) => <Text variant="small2">{row.ipAddress ?? "—"}</Text>,
  },
];

export const PageAuditLogs = () => {
  const { getAuditLogsApi } = useApi();
  const { hasLoader } = useLoaders();
  const { searchParams, setSearchParams, appendSearchParams } = useSearchParams();
  const { navigate } = useNavigation();
  const { user } = useUser();

  useEffect(() => {
    if (!hasPermission(user.roleCode, PermissionCode.AUDIT_READ)) {
      navigate(pages.accessDenied);
    }
  }, []);

  const [data, setData] = useState<ResponseGetAuditLogs | null | undefined>(undefined);

  const page = Number(searchParams["page"] || "1");
  const dateFrom = searchParams["dateFrom"] || "";
  const dateTo = searchParams["dateTo"] || "";
  const actionCode = (searchParams["actionCode"] as AuditActionCode | "") || "";
  const resourceType = (searchParams["resourceType"] as AuditResourceType | "") || "";
  const status = (searchParams["status"] as "success" | "failure" | "") || "";
  const search = searchParams["search"] || "";

  const isLoading = data === undefined || hasLoader(RouteGetAuditLogs);

  useEffect(() => {
    void (async () => {
      setData(
        await getAuditLogsApi({
          page,
          size: PAGE_SIZE,
          ...(dateFrom && { dateFrom }),
          ...(dateTo && { dateTo }),
          ...(actionCode && { actionCode }),
          ...(resourceType && { resourceType }),
          ...(status && { status }),
          ...(search && { search }),
        }),
      );
    })();
  }, [page, dateFrom, dateTo, actionCode, resourceType, status, search]);

  const setFilter = (key: FilterKey, value: string | number | undefined) => {
    appendSearchParams({ page: 1, [key]: value === "" ? undefined : value });
  };

  const dateFromValue = dateFrom ? new Date(dateFrom) : null;
  const dateToValue = dateTo ? new Date(dateTo) : null;

  return (
    <Page>
      <Flex padding="10px 0 0 0" justify="between" align="center">
        <Text variant="h3">Audit Logs</Text>
      </Flex>

      <Flex gap="8px" flexWrap="wrap" align="end">
        <DateInput
          title="From"
          value={dateFromValue}
          setValue={(date) => setFilter("dateFrom", date ? formatDateParam(date) : undefined)}
          max={dateToValue || undefined}
          width="140px"
        />

        <DateInput
          title="To"
          value={dateToValue}
          setValue={(date) => setFilter("dateTo", date ? formatDateParam(date) : undefined)}
          min={dateFromValue || undefined}
          width="140px"
        />

        <Select
          title="Action"
          options={ACTION_CODE_OPTIONS}
          value={actionCode}
          setValue={(value) => setFilter("actionCode", value)}
          isSearchable
          width="200px"
        />

        <Select
          title="Resource"
          options={RESOURCE_TYPE_OPTIONS}
          value={resourceType}
          setValue={(value) => setFilter("resourceType", value)}
          isSearchable
          width="170px"
        />

        <Select
          title="Status"
          options={STATUS_OPTIONS}
          value={status}
          setValue={(value) => setFilter("status", value)}
          width="140px"
        />

        <GlobalSearch search={search} setSearch={(value) => setFilter("search", value)} />
      </Flex>

      <Flex flexGrow column gap="10px">
        {isLoading && <PageStatus label="Loading audit logs..." icon="spinner" />}

        {!isLoading && data === null && (
          <PageStatus label="Failed to load audit logs" icon="alert" color={Color.red} />
        )}

        {!isLoading && data && data.data.length === 0 && (
          <PageStatus label="No audit log entries found" icon="empty" color={Color.spaceGray} />
        )}

        {!isLoading && data && data.data.length > 0 && (
          <SubgridTable headersNoWrap columns={AUDIT_LOG_COLUMNS} data={data.data} />
        )}
      </Flex>

      <Pagination
        page={page}
        setPage={(value) => {
          if (value !== page) {
            setSearchParams({ ...searchParams, page: value });
          }
        }}
        totalPages={data?.totalPages}
        isLoading={isLoading}
        isUsingInput
      />
    </Page>
  );
};

interface GlobalSearchProps {
  search: string;
  setSearch: (value: string) => void;
}

const GlobalSearch = ({ search, setSearch }: GlobalSearchProps) => {
  const [value, setValue] = useState(search);

  useEffect(() => {
    setValue(search);
  }, [search]);

  useDebounceEffect(
    () => {
      setSearch(value);
    },
    [value],
    700,
  );

  return (
    <Search
      placeholder="Search path or email..."
      search={value}
      setSearch={setValue}
      width="200px"
    />
  );
};

const formatDateParam = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
